import { useCallback, useEffect } from 'react';
import { Alert, Platform } from 'react-native';
import { CommonActions } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { navigationRef } from './navigationRef';
import { SET_LAST_PLAN } from '../store/constants';
import { openBillingPage } from '../store/actions/billing';
import { useAccountLimits } from '../hooks/useAccountLimits';
import type { RootState } from '../store';

// There is no plans list to come back to: the app opens the plan each person
// last had open, and the plan menu in the chat header switches between plans.

export const lastPlanScope = (accountId?: string | null, cognitoId?: string | null): string | null => (
  accountId && cognitoId ? `${accountId}:${cognitoId}` : null
);

const selectScope = (s: RootState) => lastPlanScope(s.session?.account?.account_id, s.authentication?.user?.cognito_id);

export function useLastPlanId(): number | null {
  return useSelector((s: RootState) => {
    const scope = selectScope(s);
    return scope ? s.session?.lastPlanByScope?.[scope] ?? null : null;
  });
}

/** Remember the plan on screen so the next launch opens it. */
export function useRememberPlan(projectId: number | string | null | undefined): void {
  const dispatch = useDispatch();
  const scope = useSelector(selectScope);
  useEffect(() => {
    if (!scope || projectId == null) return;
    dispatch({ type: SET_LAST_PLAN, payload: { scope, projectId } });
  }, [dispatch, scope, projectId]);
}

/**
 * Open `screen` in the Plans stack (as its only screen with `reset`) and switch
 * to the Plans tab.
 *
 * Once the Plans stack exists, the actions go to its navigators directly.
 * navigate('Main', { screen: 'Plans', params: { screen } }) leaves the target
 * in route params for each navigator to read as it renders, and React
 * Navigation marks them read without navigating when the same render also
 * changes that navigator's screens: a reset, or the launch landing replacing
 * the plans screen. The tab never switched, or a link opened from signed out
 * was dropped, with no error.
 */
export function navigateInPlans(screen: string, params?: object, options: { reset?: boolean } = {}): void {
  if (!navigationRef.isReady()) return;
  const root: any = navigationRef.getRootState();
  const focused = root?.routes?.[root.index];
  const main = focused?.name === 'Main' ? focused.state : undefined;
  const plansKey = main?.routes?.find((route: any) => route.name === 'Plans')?.state?.key;
  if (!main?.key || !plansKey) {
    (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen, params } });
    return;
  }
  const action = options.reset
    ? CommonActions.reset({ index: 0, routes: [{ name: screen, params }] })
    : CommonActions.navigate(screen, params);
  navigationRef.dispatch({ ...action, target: plansKey });
  navigationRef.dispatch({ ...CommonActions.navigate('Plans'), target: main.key });
}

/**
 * Open a plan's Ellie chat as the only screen in the Plans stack, from
 * anywhere (another plan, Account settings, the cross-plan overview), so Back
 * never returns to the previous plan's screens.
 */
export function openPlanChat(project: { id?: number | string; project_id?: number | string } & Record<string, unknown>): void {
  const projectId = project?.id ?? project?.project_id;
  if (projectId == null) return;
  navigateInPlans('ProjectChat', { projectId, project }, { reset: true });
}

/**
 * Back to the Plans root as its only screen. The root is the plan chat with no
 * plan in its params: it opens the plan this person last had open (or the
 * first-plan chat when there is none). For screens that leave a plan behind
 * (delete, a plan that is gone) so Back never returns to it.
 */
export function openPlansRoot(): void {
  navigateInPlans('ProjectChat', undefined, { reset: true });
}

/**
 * Plans over the account's limit: the oldest `maxProjects` stay open, newer
 * ones are locked until the subscription allows them. maxProjects 0 (limits
 * not loaded) and -1 (unlimited) lock nothing.
 */
export function lockedPlanIds(projects: Array<{ id?: number | string; created_at?: string }>, maxProjects: number): Set<string> {
  if (!(maxProjects > 0) || projects.length <= maxProjects) return new Set();
  const byAge = [...projects].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
  return new Set(byAge.slice(maxProjects).map((p) => String(p.id)));
}

type PlanRow = { id?: number | string; created_at?: string; updated_at?: string };

/** The plan to open on launch: the last one open if it can still be opened, else the most recently updated. */
export function pickLandingPlan<T extends PlanRow>(projects: T[], maxProjects: number, lastPlanId: number | string | null): T | null {
  const locked = lockedPlanIds(projects, maxProjects);
  const openable = projects.filter((p) => !locked.has(String(p.id)));
  if (openable.length === 0) return null;
  const updatedAt = (p: PlanRow) => new Date(p.updated_at || p.created_at || 0).getTime();
  return openable.find((p) => lastPlanId != null && String(p.id) === String(lastPlanId))
    ?? [...openable].sort((a, b) => updatedAt(b) - updatedAt(a))[0];
}

const openSettingsScreen = (screen: 'SettingsHome' | 'Billing') => {
  if (!navigationRef.isReady()) return;
  (navigationRef as any).navigate('Main', { screen: 'Settings', params: { screen } });
};

export const openAccountSettings = () => openSettingsScreen('SettingsHome');

/** New plan, unless the account is at its plan limit. */
export function useOpenNewPlan(): () => void {
  const { canAddProject, maxProjects, projectCount } = useAccountLimits();
  return useCallback(() => {
    if (!canAddProject) {
      // On iOS (App Store 3.1.1), omit the in-app upgrade CTA.
      Alert.alert(
        'Plan limit reached',
        `Your plan allows up to ${maxProjects} care plan${maxProjects !== 1 ? 's' : ''} (${projectCount} used). To create more, manage your subscription at eleplan.com.`,
        Platform.OS === 'ios'
          ? [{ text: 'OK', style: 'cancel' }]
          : [
              { text: 'Manage Plan', onPress: () => (Platform.OS === 'web' ? openSettingsScreen('Billing') : openBillingPage('/billing')) },
              { text: 'OK', style: 'cancel' },
            ],
      );
      return;
    }
    if (!navigationRef.isReady()) return;
    (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen: 'CreatePlan' } });
  }, [canAddProject, maxProjects, projectCount]);
}

/** A locked plan opens the way to unlock it. */
export function useOpenLockedPlan(): () => void {
  const { maxProjects } = useAccountLimits();
  return useCallback(() => {
    if (Platform.OS === 'ios') {
      Alert.alert(
        'Plan limit reached',
        `Your plan includes up to ${maxProjects} care plan${maxProjects !== 1 ? 's' : ''}. Manage your subscription at eleplan.com to unlock this plan.`,
        [{ text: 'OK', style: 'cancel' }],
      );
    } else if (Platform.OS === 'web') {
      openSettingsScreen('Billing');
    } else {
      openBillingPage('/billing');
    }
  }, [maxProjects]);
}
